import { useEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { CloseIcon } from "@/shared/assets/icons";
import OnboardingTooltip from "../OnboardingTooltip";

interface ModalWrapperProps {
  onClose: () => void;
  children: React.ReactNode;
  width?: string;
  ariaLabel?: string;
  isCloseDisabled?: boolean;
  closeDisabledHint?: string;
}

export default function ModalWrapper({
  onClose,
  children,
  width = "w-md",
  ariaLabel,
  isCloseDisabled = false,
  closeDisabledHint,
}: ModalWrapperProps) {
  const { t } = useTranslation("Common");

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (isCloseDisabled) return;
      const hasHigherOverlay = document.querySelector(".z-\\[10200\\]");
      if (hasHigherOverlay) return;
      onClose();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, isCloseDisabled]);

  function handleBackdropClick() {
    if (isCloseDisabled) return;
    onClose();
  }

  const closeButton = (
    <button
      type="button"
      aria-label={t("close")}
      aria-disabled={isCloseDisabled}
      className={cn(
        "btn btn-tertiary btn-icon w-8! h-8! p-0! flex items-center justify-center radius-xs content-base-secondary!",
        isCloseDisabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
      )}
      onClick={isCloseDisabled ? undefined : onClose}>
      <CloseIcon />
    </button>
  );

  return createPortal(
    <div
      className="fixed top-0 left-0 w-screen h-screen surface-backdrop blur-level-1 flex justify-center items-center z-[10001]"
      onClick={handleBackdropClick}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || t("dialog.defaultLabel")}
        className={cn(
          `
            background-static-white radius-lg prompt-box-shadow
            relative flex flex-col
            ${width} max-h-[80vh]
            p-8 gap-6
          `,
        )}
        onClick={(e) => e.stopPropagation()}>
        <div className="absolute top-8 right-8 z-10">
          {isCloseDisabled ? (
            <OnboardingTooltip
              align="right"
              className="inline-flex cursor-not-allowed"
              content={
                <div className="max-w-[220px] text-body-regular-xs content-on-background-neutral">
                  {closeDisabledHint || t("dialog.closeDisabled")}
                </div>
              }>
              {closeButton}
            </OnboardingTooltip>
          ) : (
            closeButton
          )}
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
